import React from "react";
import { Container, Typography, Box, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@material-ui/core";
import moment from "moment";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "react-simple-snackbar";
import Spinner from "../components/Spinner/Spinner";
import NoData from "../components/svgIcons/NoData";
import styles from "../components/Invoices-DataGrid/Invoices.module.css";
import { useHistory } from "react-router-dom";
import {
  getDeletedInvoices,
  restoreInvoiceById,
  deleteInvoicePermanentById,
} from "../actions/invoiceActions";

const statuses = ["Unpaid", "Paid", "Partial"];

const RecentlyDeletedPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [openSnackbar] = useSnackbar();
  const { deletedInvoices = [], isLoading } = useSelector(
    (state) => state.invoices || {}
  );
  const [permanentDeleteId, setPermanentDeleteId] = React.useState(null);

  React.useEffect(() => {
    dispatch(getDeletedInvoices());
  }, [dispatch]);

  const toCommas = (value = 0) =>
    Number(value)
      .toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  const checkStatus = (status) =>
    status === "Partial"
      ? {
          border: "solid 0px #1976d2",
          backgroundColor: "#baddff",
          padding: "8px 18px",
          borderRadius: "20px",
        }
      : status === "Paid"
      ? {
          border: "solid 0px green",
          backgroundColor: "#a5ffcd",
          padding: "8px 18px",
          borderRadius: "20px",
        }
      : status === "Unpaid"
      ? {
          border: "solid 0px red",
          backgroundColor: "#ffaa91",
          padding: "8px 18px",
          borderRadius: "20px",
        }
      : "red";

  const handleRestore = (id) => {
    dispatch(restoreInvoiceById(id, openSnackbar)).then(() =>
      dispatch(getDeletedInvoices())
    );
  };

  const openInvoice = (id) => history.push(`/invoice/${id}`);

  const columns = [
    {
      field: "client",
      headerName: "Client",
      type: "singleSelect",
      valueOptions: deletedInvoices
        .map((inv) => inv?.client?.store)
        .filter(Boolean),
      editable: false,
      minWidth: 125,
      flex: 1,
      valueGetter: (params) => params.row?.client?.store || "Unknown",
    },
    {
      field: "total",
      headerName: "Amount",
      editable: false,
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => `$${toCommas(params.value || 0)}`,
    },
    {
      field: "receivedByDate",
      headerName: "Received Date",
      flex: 1,
      minWidth: 150,
      type: "date",
      valueGetter: (params) =>
        params.value ? new Date(params.value) : null,
    },
    {
      field: "dueDate",
      headerName: "Due Date",
      flex: 1,
      minWidth: 150,
      type: "date",
      valueGetter: (params) =>
        params.value ? new Date(params.value) : null,
    },
    {
      field: "status",
      type: "singleSelect",
      valueOptions: statuses,
      headerName: "Status",
      width: 130,
      renderCell: (params) => (
        <button style={checkStatus(params.value)}>{params.value}</button>
      ),
    },
    {
      field: "deletedAt",
      headerName: "Deleted On",
      type: "date",
      flex: 1,
      minWidth: 160,
      valueGetter: (params) =>
        params.value ? new Date(params.value) : null,
      valueFormatter: ({ value }) =>
        value ? moment(value).format("MMM DD, YYYY") : "—",
    },
    {
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 260,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div style={{ display: "flex", gap: 10 }}>
          <Button
            style={{ color: "white", backgroundColor: "green" }}
            onClick={(e) => {
              e.stopPropagation();
              handleRestore(params.row._id);
            }}
            variant="contained"
          >
            Restore
          </Button>
          <Button
            style={{ color: "white", backgroundColor: "red" }}
            onClick={(e) => {
              e.stopPropagation();
              setPermanentDeleteId(params.row._id);
            }}
            variant="contained"
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        style={{ paddingTop: 40 }}
      >
        <Spinner />
      </Box>
    );
  }

  if (deletedInvoices.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          paddingTop: "20px",
          margin: "80px",
        }}
      >
        <NoData />
        <p style={{ padding: "40px", color: "gray", textAlign: "center" }}>
          No invoices have been deleted recently.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.pageLayout}>
      <Container style={{ maxWidth: 1600 }}>
        <div className={styles.buttons}>
          <Typography variant="h4" component="h1" style={{ paddingTop: 10 }}>
            Recently Deleted Invoices
          </Typography>
        </div>
        <div className={styles.gridLayout}>
          <DataGrid
            sx={{
              "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                py: "6px",
              },
              "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                py: "8px",
              },
              "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                py: "12px",
              },
            }}
            autoHeight
            rows={deletedInvoices}
            columns={columns}
            getRowId={(row) => row._id}
            pageSize={20}
            components={{ Toolbar: GridToolbar }}
            disableSelectionOnClick
            onRowClick={(row) => openInvoice(row.id)}
            sortingOrder={["desc", "asc"]}
            initialState={{
              sorting: {
                sortModel: [
                  {
                    field: "deletedAt",
                    sort: "desc",
                  },
                ],
              },
            }}
          />
        </div>
      </Container>
      <Dialog
        open={Boolean(permanentDeleteId)}
        onClose={() => setPermanentDeleteId(null)}
      >
        <DialogTitle>Delete Invoice Permanently</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This action will remove the invoice permanently from the system. This cannot be undone. Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermanentDeleteId(null)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (permanentDeleteId) {
                dispatch(deleteInvoicePermanentById(permanentDeleteId, openSnackbar)).then(() => {
                  dispatch(getDeletedInvoices());
                });
              }
              setPermanentDeleteId(null);
            }}
            color="secondary"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default RecentlyDeletedPage;
